import { NavigationBar } from '@travelnest/hui';
import * as React from 'react';
import { Navigate } from 'react-router-dom';

import { Rule } from '@travelnest/merlin.rule';

import { ICommission } from '../../../../data/entities/commission';
import { ICommissionChannels } from '../../../../data/entities/commission-channels';
import { IProperty } from '../../../../data/entities/property';
import { selectableChannels } from '../../../../entities/contracts/constants';
import { IChannel } from '../../../../entities/contracts/types';
import { CancellationPreferences, CancellationPreferenceTypes } from '../../../../entities/policies/constants';
import { IChannelPolicy, IPolicies } from '../../../../entities/policies/types';
import DefinitionList from '../../../../view/components/definition-list/DefinitionList';
import { FormLabel, FormLabelHelp, FormRow, FormSelect } from '../../../../view/components/form';
import { Loader } from '../../../../view/components/loader/Loader';
import { Content } from '../../../../view/components/page';
import { Panel } from '../../../../view/components/panel/Panel';
import { DirectListingLinks } from './DirectListingLinks';

interface IMarketingChannelComponentProps {
	loading: boolean;
	channel: string;
	properties: IProperty[];
	policy: string;
	channels: IChannel[];
	policies: IPolicies;
	commissions: ICommission;
	commissionChannels: ICommissionChannels;
	isLegacyCommission: boolean;
}

const joinPolicyPanels = (panels: JSX.Element[]): JSX.Element[] =>
	panels.length === 1 ? panels : [panels[0], <Rule />, ...(joinPolicyPanels(panels.slice(1)))];

export const MarketingChannelComponent: React.FC<IMarketingChannelComponentProps> = (props) => {
	const { channel,
		properties,
		policy: selected,
		channels,
		policies,
		loading,
		commissions,
		commissionChannels,
		isLegacyCommission } = props;

	const info = channels.find((ch) => ch.ref === channel);

	if (!info) return <Navigate to="/marketing" />;

	if (loading) return <Loader loading={true} />;

	document.title = `${info.display_name} information | Travelnest`;

	const channelPolicy = policies[info.id as selectableChannels];
	const [displayPolicy, setDisplayPolicy] = React.useState(selected);

	let selectedPolicy: IChannelPolicy | IChannelPolicy[] | undefined = channelPolicy.policies[displayPolicy || selected];

	let selectedPolicyAlertPanel;
	let selectedPolicyPanel;
	if (!selectedPolicy) {
		selectedPolicy = channelPolicy.policies[CancellationPreferenceTypes.FLEXIBLE];
		selectedPolicyAlertPanel =
			<Panel type="error" withIcon={true}>
				This policy is currently unsupported on {info.display_name}. You have been set to flexible.
			</Panel>;
	}
	if (Array.isArray(selectedPolicy)) {
		selectedPolicyPanel = joinPolicyPanels(selectedPolicy.map((pol) => <SelectedPolicy selectedPolicy={pol} />));
	} else if (selectedPolicy) {
		selectedPolicyPanel = <SelectedPolicy selectedPolicy={selectedPolicy} />;
	}

	const commissionRate = isLegacyCommission && channel === 'bookingcom' && commissionChannels
		? commissionChannels?.['booking.com'].presented_total_rate * 100
		: commissions?.presented_total_rate * 100;

	const isDirect = channel === 'direct' || channel === 'travelnest';

	return (
		<div className="portal-wrapper">
			<NavigationBar title={info.display_name} />
			<div className="marketing-channel">
				<div className="marketing-channel__left">
					<Panel withIcon={true}>
						The cancellation rules for this marketing channel are set by your overall cancellation preferences.
						They cannot be changed individually at this time.
					</Panel>
					<FormRow>
						<FormLabel htmlFor="rule-selector">Cancellation rules</FormLabel>
						<FormLabelHelp>This is determined by your cancellation preferences.</FormLabelHelp>
						<FormSelect disabled id="rule-selector">
							<option>{CancellationPreferences[selected]}</option>
						</FormSelect>
					</FormRow>
					{isDirect && <DirectListingLinks properties={properties} channels={channels} />}
					{channelPolicy.faqs && <>
						<h2 className="marketing-channel__faqs-title">FAQs</h2>
						<DefinitionList defs={channelPolicy.faqs} />
					</>}
				</div>
				<div className="marketing-channel__right">
					<Content type="tile">
						<div className="marketing-channel__right-content">
							<div className={`marketing-channel__logo marketing-channel__logo--${info.ref}`}>
								<img src={info.whiteLogo} />
							</div>
							{isLegacyCommission && <>
								<div className="marketing-channel__stat">
									<h4 className="marketing-channel__stat-heading">Total fees</h4>
									<dl className="marketing-channel__stat-list">
										<dt className="marketing-channel__stat-title">{commissionRate}%</dt>
										<dd className="marketing-channel__stat-text">+ VAT *</dd>
									</dl>
									<p>* only applicable to UK customers </p>
								</div>
								<Rule />
							</>
							}
							<div className="marketing-channel__available-policies">
								<FormLabel htmlFor="available-policies">View available cancellation rules</FormLabel>
								<FormSelect id="available-policies" value={displayPolicy} onChange={(e) => setDisplayPolicy(e.target.value)}>
									{Object.values(CancellationPreferenceTypes).map((p) => {
										return <option
											disabled={(!Object.keys(channelPolicy.policies).includes(p))}
											value={p}
											key={p}>{CancellationPreferences[p]}</option>;
									})}
								</FormSelect>
							</div>
							{selectedPolicyAlertPanel}
							{Array.isArray(selectedPolicy) && <Panel>
								Guest can choose between these options
							</Panel>}
							{selectedPolicyPanel}
						</div>
					</Content>
				</div>
			</div>
		</div>
	);
};

interface ISelectedPolicyProps {
	selectedPolicy: IChannelPolicy;
}

const SelectedPolicy: React.FC<ISelectedPolicyProps> = ({ selectedPolicy }) => {
	return <>
		{selectedPolicy.uplift && <div className="marketing-channel__stat">
			<h4 className="marketing-channel__stat-heading">We uplift price</h4>
			<dl className="marketing-channel__stat-list">
				{selectedPolicy.uplift.map((period, i) => {
					return <React.Fragment key={i}>
						{('amount' in period) && <dt className="marketing-channel__stat-title">{period.amount}%</dt>}
						<dd className="marketing-channel__stat-text">{period.period}</dd>
					</React.Fragment>;
				})}
			</dl>
		</div>}

		<div className="marketing-channel__stat">
			<h4 className="marketing-channel__stat-heading">Guest refund amount</h4>
			<dl className="marketing-channel__stat-list">
				{selectedPolicy.refund.map((period, i) => {
					return <React.Fragment key={i}>
						{('amount' in period) && <dt className="marketing-channel__stat-title">{period.amount}%</dt>}
						<dd className="marketing-channel__stat-text">{period.period}</dd>
					</React.Fragment>;
				})}
			</dl>
		</div>

		<div className="marketing-channel__stat">
			<h4 className="marketing-channel__stat-heading">Payment made to you</h4>
			<dl className="marketing-channel__stat-list">
				{selectedPolicy.ownerPaid.map((period, i) => {
					return <React.Fragment key={i}>
						{('amount' in period) && <dt className="marketing-channel__stat-title">100%</dt>}
						<dd className="marketing-channel__stat-text">{period.period}</dd>
					</React.Fragment>;
				})}
			</dl>
		</div>
	</>;
};
